import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackspace, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import ModalShell from '../ModalShell/ModalShell';
import { useI18n } from '../../i18n/I18nContext';
import './PinPadModal.css';

interface PinPadModalProps {
  email: string;
  displayName?: string;
  loading: boolean;
  onClose: () => void;
  onSubmit: (pin: string) => Promise<void>;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const PinPadModal: React.FC<PinPadModalProps> = ({ email, displayName, loading, onClose, onSubmit, showToast }) => {
  const { t } = useI18n();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [revealingPin, setRevealingPin] = useState(false);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key >= '0' && event.key <= '9') {
        setPin((current) => (current + event.key).slice(0, 6));
        return;
      }
      if (event.key === 'Backspace') {
        setPin((current) => current.slice(0, -1));
        return;
      }
      if (event.key === 'Delete') {
        setPin('');
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleSubmit();
        return;
      }
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [email, loading, onClose, onSubmit, pin, t]);

  async function handleSubmit() {
    if (!email.trim()) {
      setError(t('login.pinEmailRequired'));
      return;
    }
    if (!pin.trim()) {
      setError(t('login.pinRequired'));
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      setError(t('profile.invalidPin'));
      return;
    }
    setError(null);
    try {
      await onSubmit(pin);
      onClose();
    } catch (nextError) {
      const message =
        nextError instanceof Error && nextError.message.toLowerCase().includes('invalid credentials')
          ? t('login.invalidCredentials')
          : nextError instanceof Error
            ? nextError.message
            : t('login.error');
      setError(message);
      showToast(message, 'error');
    }
  }

  return (
    <ModalShell onClose={onClose} ariaLabel={t('login.pinTitle')} className="modal pin-pad-modal">
      <h3 style={{ marginTop: 0 }}>{t('login.pinTitle')}</h3>
      <p className="pin-pad-description">{t('login.pinDescription')}</p>
      <div className="pin-pad-email">{displayName || email || '-'}</div>
      <div className="pin-pad-password-input">
        <input
          type="text"
          className="pin-pad-hidden-username"
          value={email}
          autoComplete="username"
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />
        <input
          type="text"
          className="pin-pad-input"
          value={pin ? (revealingPin ? pin : '•'.repeat(pin.length)) : ''}
          placeholder="----"
          readOnly
          inputMode="numeric"
          aria-label={t('login.pin')}
        />
        <button
          type="button"
          className="pin-pad-input-toggle"
          onPointerDown={(event) => {
            event.preventDefault();
            setRevealingPin(true);
          }}
          onPointerUp={() => setRevealingPin(false)}
          onPointerLeave={() => setRevealingPin(false)}
          onPointerCancel={() => setRevealingPin(false)}
          onBlur={() => setRevealingPin(false)}
          onKeyDown={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              setRevealingPin(true);
            }
          }}
          onKeyUp={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              setRevealingPin(false);
            }
          }}
          aria-label={revealingPin ? t('passwordInput.hide') : t('passwordInput.show')}
          title={revealingPin ? t('passwordInput.hide') : t('passwordInput.show')}
        >
          <FontAwesomeIcon icon={revealingPin ? faEyeSlash : faEye} />
        </button>
      </div>
      {error ? <div className="pin-pad-error">{error}</div> : null}
      <div className="pin-pad-grid">
        {digits.slice(0, 9).map((digit) => (
          <button key={digit} type="button" className="pin-pad-key" onClick={() => setPin((current) => (current + digit).slice(0, 6))}>
            {digit}
          </button>
        ))}
        <button type="button" className="pin-pad-key pin-pad-key-secondary" onClick={() => setPin('')}>
          C
        </button>
        <button type="button" className="pin-pad-key" onClick={() => setPin((current) => (current + '0').slice(0, 6))}>
          0
        </button>
        <button type="button" className="pin-pad-key pin-pad-key-secondary" onClick={() => setPin((current) => current.slice(0, -1))}>
          <FontAwesomeIcon icon={faBackspace} />
        </button>
      </div>
      <div className="pin-pad-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          {t('auth.cancel')}
        </button>
        <button type="button" className="pin-pad-submit" onClick={() => void handleSubmit()} disabled={loading}>
          {t('login.pinSignIn')}
        </button>
      </div>
    </ModalShell>
  );
};

export default PinPadModal;
